// @spec DHB-STR-020
// @spec DHB-STR-021
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import type { Integration } from '@/features/datahub/integrations';
import { ConfigureIntegrationDialog, IntegrationsList } from '@/features/datahub/integrations';
import { useOrganizationId } from '@/hooks/use-organization-id';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/datahub/integrations/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.integrations'),
  }),
  component: IntegrationsPage,
});

/**
 * Integrations catalog page.
 *
 * Shows a browseable grid of available integrations that users
 * can configure to sync data into the Data Hub.
 */
function IntegrationsPage() {
  const orgId = useOrganizationId();
  const { isAdmin, isLoading: isLoadingRole } = useOrgRole();
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Handle configure click - open the configuration dialog
  const handleConfigureClick = (integration: Integration) => {
    setSelectedIntegration(integration);
    setDialogOpen(true);
  };

  // Handle dialog close
  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      // Clear selection when dialog closes
      setSelectedIntegration(null);
    }
  };

  // Show skeleton while loading org info
  if (isLoadingRole || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <header>
            <Skeleton className="h-9 w-48" />
            <Skeleton className="h-5 w-96 mt-2" />
          </header>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">Integrations</h1>
          <p className="mt-1 text-muted-foreground">
            Browse and configure integrations to sync data from third-party systems
          </p>
        </header>

        <IntegrationsList onConfigureClick={handleConfigureClick} isAdmin={isAdmin} />
      </div>

      {/* Configuration dialog */}
      {selectedIntegration && (
        <ConfigureIntegrationDialog
          integration={selectedIntegration}
          open={dialogOpen}
          onOpenChange={handleDialogOpenChange}
        />
      )}
    </div>
  );
}
